import {
  InboundReferenceCollection,
  InventoryBinLocationDoc,
  InventoryPurchaseConfig,
  Product,
  ProductInboundTransaction,
  ProductInventory,
  ProductInventoryDoc,
  ProductInventoryUnit,
  ProductSupplierDoc,
  ProductSupplierInventoryReceipt,
  ProductSupplierTransactionCheckItem,
  ProductTransaction,
  ProductTransactionStatus,
  PurchaseBundleType,
} from '@gooduncles/gu-app-schema';
import { NewValueParams, ValueFormatterParams, ValueSetterParams } from 'ag-grid-community';
import { message, notification } from 'antd';
import { Dayjs } from 'dayjs';
import { deleteField } from 'firebase/firestore';
import { countBy, get, set } from 'lodash-es';
import { FEATURES_WITH_PRIORITY, purchaseBundleToKr } from 'src/constants';

import { errorObjectToString, moveItemsToFrontInArray } from 'src/utils/common';
import { formatDate } from 'src/utils/date-util';
import {
  createProductInventory,
  getProductSupplierInventoryReceiptId,
  setDailyInventoryChanges,
  updateInventoryBinLocationItem,
  updateInventoryUnitBarcode,
  updateProductInventoryForField,
} from 'src/utils/firebase/firebase-short-cut';

import { callSupplierInboundTransactions } from './firebase/firebase-callable';
import { ProcessedOutboundTransaction, compareBinLocation } from './transaction-util';

const defaultSupplierId = 'mceT7gP1a893gy4LhyzB';

/**
 * 상품명과 브랜드, 특징, 원산지를 조합하여 재고명을 생성한다.
 */
export const getInventoryNameForProduct = (sources: Pick<Product, 'name' | 'brand' | 'features' | 'origin'>) => {
  const orderedFeatures = moveItemsToFrontInArray(sources.features ?? [], FEATURES_WITH_PRIORITY);
  const subNameList = [sources.brand, ...orderedFeatures, sources.origin].filter((item) => item && item.length > 0);
  const suffix = subNameList.length > 0 ? `(${subNameList.join('/')})` : '';
  return `${sources.name}${suffix}`;
};

/**
 * 새로 생성되는 상품의 재고를 생성 및 초기화한다.
 */
export const createProductInventoryForInit = async (
  inventoryId: string,
  product: Product,
  customInventoryName?: string,
  supplierId?: string
) => {
  const inventory: ProductInventory = {
    inventoryName: customInventoryName ?? getInventoryNameForProduct(product),
    quantity: Infinity,
    uom: {
      [ProductInventoryUnit.EA]: {
        ratio: 1,
        cost: product.cost,
        barcode: null,
      },
    },
    purchaseConfig: {
      supplierId: supplierId ?? defaultSupplierId,
      purchaseProductName: customInventoryName ?? product.fullName,
      purchaseBundle: PurchaseBundleType.ALL,
      purchaseMaxBundle: null,
      purchaseMultiplyUnit: 1,
      purchaseUnit: product.packing === 'box' ? '박스' : '개',
    },
    deletedAt: null,
    active: true,
    optimalStockMemo: null,
  };
  await createProductInventory(inventoryId, inventory, { bBatch: true });
};

/**
 * 묶음과 수량 목록을 메시지로 변환한다.
 * @param bundleList [묶음, 수량][]
 * @param purchaseUnit 단위 (g, kg, ...)
 * @returns
 */
export const toQuantityMessage = (
  bundleList: { remainder: number; amount: number; bundle: number }[],
  purchaseUnit: string
) => {
  const volumeByStore = bundleList.reduce((acc, i) => {
    if (acc[i.remainder]) {
      acc[i.remainder] = acc[i.remainder] + 1;
    } else {
      acc[i.remainder] = 1;
    }

    if (acc[i.bundle]) {
      acc[i.bundle] = acc[i.bundle] + i.amount;
    } else {
      acc[i.bundle] = i.amount;
    }

    return acc;
  }, {} as Record<number, number>);

  return Object.entries(volumeByStore)
    .filter(([bundle, amount]) => Number(bundle) > 0 && amount > 0)
    .map(([bundle, amount]) => `${bundle}${purchaseUnit} x ${amount}`)
    .join('\n');
};

/**
 * 발주 설정에 따른 미리보기 메시지를 생성합니다.
 * @param config 발주 설정
 * @param volume 예시 수량 (기본값: 10)
 */
export const getPurchasePreview = (config: InventoryPurchaseConfig, volume = 10) => {
  const { purchaseProductName, purchaseBundle, purchaseMaxBundle, purchaseMultiplyUnit, purchaseUnit } = config;
  const unit = purchaseUnit ?? '개';

  if (purchaseBundle === PurchaseBundleType.ALL) {
    return `${purchaseProductName} ${volume * purchaseMultiplyUnit}${unit}`;
  }

  if (purchaseBundle === PurchaseBundleType.STORE) {
    const sample = [1, 2, 3, 4];
    const total = sample.reduce((acc, cur) => acc + cur * purchaseMultiplyUnit, 0);
    return `${purchaseProductName} ${sample.map((s) => s * purchaseMultiplyUnit).join(', ')} (총 ${total}${unit})`;
  }

  if (purchaseBundle === PurchaseBundleType.STORE_GROUP) {
    const sample = [1, 1, 2, 2, 4];
    const countByQuantity = countBy(sample);
    const quantityMessage = Object.entries(countByQuantity)
      .sort()
      .map(([quantity, count]) => `${quantity}${unit} x ${count}`)
      .join('\n');
    const total = sample.reduce((acc, cur) => acc + cur * purchaseMultiplyUnit, 0);
    return `${purchaseProductName}\n${quantityMessage}\n(총 ${total}${unit})`;
  }

  if (purchaseBundle === PurchaseBundleType.MAX && purchaseMaxBundle) {
    const totalVolume = volume * purchaseMultiplyUnit;
    const bundle = purchaseMaxBundle;
    const amount = Math.floor(totalVolume / bundle);
    const remainder = totalVolume % bundle;
    const quantityMessage = toQuantityMessage([{ remainder, amount, bundle }], unit);
    return `${purchaseProductName}\n${quantityMessage}`;
  }

  return '';
};

export type SupplierInboundData = {
  id: string;
  /** `${inventoryId}-${unit}` */
  key: string;
  inventoryId: string;
  inventoryName: string;
  unit: ProductInventoryUnit;
  ratio: number;
  requestValue: number;
};

/**
 * 거래처 입고 요청 정보로 변환합니다.
 * @param date 요청일
 * @param referenceId 참조 ID
 * @param item 거래처 입고 요청 정보
 * @returns 거래처 입고 요청 정보
 */
const createSupplierInboundTransaction = (date: string, referenceId: string, item: SupplierInboundData) => {
  // 상품 입출고 정보
  const productTransaction: ProductTransaction = {
    requestDate: date,
    status: ProductTransactionStatus.PENDING,
    inventoryId: item.inventoryId,
  };

  const transaction: ProductInboundTransaction & { _id: string } = {
    _id: item.id,
    ...productTransaction,
    referenceCollection: InboundReferenceCollection.PRODUCT_SUPPLIER,
    referenceId,
    inventoryUnit: item.unit,
    inventoryUnitRatio: item.ratio,
    requestValue: item.requestValue,
  };
  return transaction;
};

/**
 * 거래처 입고 요청을 진행합니다.
 */
export const requestSupplierInbound = async (
  supplierId: string,
  date: Dayjs,
  userId: string,
  items: SupplierInboundData[],
  checkList: ProductSupplierTransactionCheckItem[]
) => {
  const transactionDate = formatDate(date.toDate(), 'yyyy-MM-dd');
  const supplierInventoryReceiptId = getProductSupplierInventoryReceiptId();
  const inboundTransactions = items.map((item) =>
    createSupplierInboundTransaction(transactionDate, supplierInventoryReceiptId, item)
  );
  const zeroRatioTransactions = inboundTransactions.filter((i) => i.inventoryUnitRatio === 0);
  if (zeroRatioTransactions.length > 0) {
    throw new Error(
      `재고 단위 비율 값이 없는 요청이 있습니다. ${zeroRatioTransactions.map((i) => i.inventoryId).join(', ')}`
    );
  }
  const supplierInventoryReceipt: ProductSupplierInventoryReceipt & { _id: string } = {
    _id: supplierInventoryReceiptId,
    supplierId,
    transactionDate,
    createdBy: userId,
    checkList,
    inboundTransactionIds: inboundTransactions.map((i) => i._id),
    deletedAt: null,
  };
  const result = await callSupplierInboundTransactions({
    body: {
      receipt: supplierInventoryReceipt,
      inboundTransactions,
    },
  });
  return result;
};

/**
 * 재고 단위를 정렬합니다.
 */
export const sortUomUnit = (units: string[]) => {
  return units.sort((a, b) => {
    if (a === 'ea') return -1;
    if (b === 'ea') return 1;
    return a.localeCompare(b);
  }) as ProductInventoryUnit[];
};

export const inventoryPurchaseConfigTooltipMap = {
  purchaseProductName: `설정한 값으로 발주서에 상품을 표현합니다.
  ex) '굿송이 3번'으로 입력한 경우 '새송이버섯(상급/박스/2kg/국내산)' => '굿송이 3번'`,
  purchaseBundle: `발주시 분할 단위를 어떻게 계산할 것인지 정한다.
    all: 전체 주문량으로 발주한다.
    store: 매장별 주문량으로 발주한다.
    max: 발주 가능 최대 묶음으로 계산해서 발주한다.
    store-group: 매장별 주문량으로 발주하되 같은 수량끼리 묶어서 표현한다.
  `,
  purchaseMaxBundle: `발주 가능 최대 묶음
  null인 경우 매장별 주문량 또는 전체 주문량으로 발주한다.
  ex) 값이 3일 때 5개의 주문이 들어오면 => 3x1 + 2x1`,
  purchaseMultiplyUnit: `발주 단위 변환 값
  ex) 1000: 1kg 상품을 0.5개 주문한 경우 -> 0.5 * 1000 = 500
  ex) 5: 백오이(5개/국내산) 상품을 2개 주문한 경우 -> 2 * 5 = 10`,
  purchaseUnit: `발주 단위
  : '개' | 'kg' | 'g' | '박스' | '팩' | '봉지' | '포기' | '판' | '캔' | '병'
  `,
};

export type InventoryBinLocationTableRowData = ProductInventoryDoc & {
  productSupplier: ProductSupplierDoc | null;
  binLocation: string | null;
};
export const mergeProductInventoryWithBinLocation = (
  productInventories: ProductInventoryDoc[],
  productSuppliers: ProductSupplierDoc[],
  binLocationConfig: InventoryBinLocationDoc | undefined
) => {
  const { inventoryBinLocation } = binLocationConfig ?? {};
  const rowData: InventoryBinLocationTableRowData[] = productInventories
    .map((productInventory) => {
      const productSupplier =
        productSuppliers.find(
          (productSupplier) => productSupplier._id === productInventory.purchaseConfig.supplierId
        ) ?? null;
      const binLocation = inventoryBinLocation?.[productInventory._id] ?? null;
      return {
        ...productInventory,
        productSupplier,
        binLocation,
      };
    }) // 4순위 - 거래처명
    .sort((a, b) => a.productSupplier?.name?.localeCompare(b.productSupplier?.name || '') ?? 0)
    // 3순위 - 상품명
    .sort((a, b) => a.inventoryName.localeCompare(b.inventoryName))
    // 2순위 - 재고 위치
    .sort((a, b) => compareBinLocation(a.binLocation, b.binLocation))
    // 1순위 - 구분(E, A, B, C, D)
    .sort((a, b) => {
      // 먼저, groupId이 undefined인 경우를 처리
      if (a.productSupplier?.groupId === undefined && b.productSupplier?.groupId !== undefined) {
        return 1; // a가 undefined이면, b보다 뒤에 위치
      } else if (a.productSupplier?.groupId !== undefined && b.productSupplier?.groupId === undefined) {
        return -1; // b가 undefined이면, a가 b보다 앞에 위치
      }

      if (a.productSupplier?.groupId === 'E' && b.productSupplier?.groupId !== 'E') {
        // a가 'E'라면, 항상 b보다 앞서도록 함
        return -1;
      } else if (a.productSupplier?.groupId !== 'E' && b.productSupplier?.groupId === 'E') {
        // b가 'E'라면, 항상 a보다 앞서도록 함
        return 1;
      }
      return (a.productSupplier?.groupId ?? '').localeCompare(b.productSupplier?.groupId ?? '') ?? 0;
    });
  return rowData;
};

/**
 * 다음 형식을 허용합니다:
 * 맨 앞은 반드시 대문자로 시작하고, 이후에는 하이픈(-)으로 구분된 대문자와 숫자의 자유로운 조합 허용
 * 예시: A, A-1, A-123, A-123-11, A-123-1A, A-AA, A-A1-A2
 */
const binLocationRegex = /^[A-Z]((-[A-Z0-9]+)*)$/;
export const validateBinLocation = (binLocation: string) => {
  const isValid = binLocationRegex.test(binLocation);
  if (!isValid) {
    notification.error({
      message: '위치 정보 형식이 올바르지 않습니다.',
      description:
        '다음 형식 중 하나를 사용해야 합니다:\n1. 맨 앞은 반드시 대문자로 시작\n2. 그 이후에는 하이픈(-)으로 구분된 대문자와 숫자의 자유로운 조합 허용\n예시: A, A-1, A-123, A-123-11, A-123-1A, A-AA, A-A1-A2',
    });
  }
  return isValid;
};

/**
 * 재고 필드 수정 옵션
 */
interface InventoryFieldAgValueSetterOptions {
  /** 삭제 허용 여부 */
  nullable?: boolean;
  /** 값 정제 */
  sanitizer?: (value: string) => string | number;
  /** 값 유효성 검사 */
  validator?: (value: any) => boolean;
}

/**
 * ag-grid 재고 필드 수정 옵션
 */
export const inventoryFieldAgValueSetter = <T extends InventoryBinLocationTableRowData | ProcessedOutboundTransaction>(
  params: ValueSetterParams<T>,
  options: InventoryFieldAgValueSetterOptions = {
    nullable: false,
  }
) => {
  const value: string | null = params.newValue;
  const field = params.colDef.field;
  const { nullable, sanitizer, validator } = options;
  if (!field) {
    message.error('필드 정보가 없습니다.');
    return false;
  }
  // 삭제 허용 여부
  if (value === null) {
    if (!nullable) {
      message.error('삭제 할 수 없는 값입니다.');
      return false;
    }
    set(params.data, field, value);
    return true;
  }

  const sanitizedValue = value && sanitizer ? sanitizer(value) : value;
  const isValid = validator?.(sanitizedValue) ?? true;
  // 유효하지 않은 경우 수정 취소
  if (!isValid) {
    return false;
  }
  set(params.data, field, sanitizedValue);
  return true;
};

/**
 * 바코드 셀이 수정 가능한지 여부를 반환한다.
 */
export const isEditableBarcodeCell = (params: any) => {
  const {
    data,
    colDef: { field },
  } = params;
  const value = get(data, field);
  if (field === 'uom.ea.barcode' && value !== undefined) {
    return true;
  } else if (field === 'uom.bundle.barcode' && value !== undefined) {
    return true;
  }
  message.error('재고 설정이 안된 단위 입니다.');
  return false;
};

/**
 * 매입가 셀이 수정 가능한지 여부를 반환한다.
 */
export const isEditableCostCell = (params: any) => {
  const {
    data,
    colDef: { field },
  } = params;
  const value = get(data, field);
  if (field === 'uom.ea.cost' && value !== undefined) {
    return true;
  } else if (field === 'uom.bundle.cost' && value !== undefined) {
    return true;
  }
  message.error('재고 설정이 안된 단위 입니다.');
  return false;
};

/**
 * 재고 필드 값이 변경되었을 때 처리한다.
 */
export const onCellValueChangedForInventory = async (
  params: NewValueParams<InventoryBinLocationTableRowData>,
  onValueChanged: (id: string, value: string | null) => Promise<string>
) => {
  const {
    newValue,
    oldValue,
    data,
    colDef: { field },
  } = params;
  if (newValue === oldValue) return;
  const { _id } = data;
  try {
    await onValueChanged(_id, newValue);
    message.success(newValue ? '변경완료.' : '삭제완료.', 1);
  } catch (error) {
    console.error(error);
    const description = errorObjectToString(error);
    notification.error({
      message: `재고의 ${field} 변경에 실패했습니다.`,
      description,
    });
  }
};

/**
 * 발주 묶음 방식 값 포맷터
 */
export const purchaseBundleValueFormatter = (params: ValueFormatterParams<ProductInventoryDoc, PurchaseBundleType>) => {
  const { value } = params;
  return purchaseBundleToKr[value] ?? '';
};

export type InventoryTableRowData = ProductInventoryDoc & {
  productSupplier: ProductSupplierDoc | null;
  binLocation: string | null;
};

/**
 * InventoryTable의 셀 편집 관련 핸들러 모음
 * - valueSetter: 셀 값 변경 시 호출되는 함수
 * - onCellValueChanged: 셀 값 변경 후 호출되는 함수
 */
export const InventoryTableHandlers = {
  /**
   * 적정 재고 보유량 필드 관련 핸들러
   */
  optimalStockMemo: {
    valueSetter: (params: ValueSetterParams<InventoryTableRowData>) =>
      inventoryFieldAgValueSetter(params, {
        nullable: true,
      }),
    onCellValueChanged: (params: NewValueParams<InventoryTableRowData>) =>
      onCellValueChangedForInventory(params, (id, value) =>
        updateProductInventoryForField(id, 'optimalStockMemo', value)
      ),
  },
  /**
   * 재고명 필드 관련 핸들러
   */
  inventoryName: {
    valueSetter: (params: ValueSetterParams<InventoryTableRowData>) => inventoryFieldAgValueSetter(params),

    onCellValueChanged: (params: NewValueParams<InventoryTableRowData>) =>
      onCellValueChangedForInventory(params, (id, value) => updateProductInventoryForField(id, 'inventoryName', value)),
  },

  /**
   * 재고 위치 필드 관련 핸들러
   */
  binLocation: {
    valueSetter: (params: ValueSetterParams<InventoryTableRowData>) =>
      inventoryFieldAgValueSetter(params, {
        nullable: true,
        sanitizer: (value) => value.trim(),
        // validator: (value: string) => validateBinLocation(value),
      }),

    onCellValueChanged: (params: NewValueParams<InventoryTableRowData>) =>
      onCellValueChangedForInventory(params, updateInventoryBinLocationItem),
  },

  /**
   * 바코드 필드 관련 핸들러
   */
  barcode: {
    valueSetter: (params: ValueSetterParams<InventoryTableRowData>) =>
      inventoryFieldAgValueSetter(params, {
        nullable: true,
      }),

    onCellValueChanged: (unit: ProductInventoryUnit) => (params: NewValueParams<InventoryTableRowData>) =>
      onCellValueChangedForInventory(params, (id, value) => updateInventoryUnitBarcode(id, unit, value)),
  },

  /**
   * 매입가 필드 관련 핸들러
   */
  cost: {
    valueSetter: (params: ValueSetterParams<InventoryTableRowData>) =>
      inventoryFieldAgValueSetter(params, {
        nullable: false,
        sanitizer: (value) => Number(value),
        validator: (value) => (isNaN(value) ? false : true),
      }),

    onCellValueChanged: (params: NewValueParams<InventoryTableRowData>) =>
      onCellValueChangedForInventory(params, (id, value) =>
        params.colDef.field
          ? updateProductInventoryForField(id, params.colDef.field, value)
          : Promise.reject(new Error('필드 정보가 없습니다.'))
      ),
  },

  /**
   * 발주 설정 필드 관련 핸들러
   */
  purchaseConfig: {
    valueSetter: (params: ValueSetterParams<InventoryTableRowData>) =>
      inventoryFieldAgValueSetter(params, {
        nullable: [
          'purchaseConfig.purchaseProductName',
          'purchaseConfig.purchaseMaxBundle',
          'purchaseConfig.purchaseUnit',
        ].includes(params.colDef.field ?? ''),
        sanitizer: (value) => {
          const field = params.colDef.field ?? '';
          if (['purchaseConfig.purchaseMaxBundle', 'purchaseConfig.purchaseMultiplyUnit'].includes(field)) {
            return Number(value);
          }
          return value;
        },
        validator: (value) => {
          // purchaseMaxBundle, purchaseMultiplyUnit 필드는 숫자인지 검증
          const field = params.colDef.field ?? '';
          if (['purchaseConfig.purchaseMaxBundle', 'purchaseConfig.purchaseMultiplyUnit'].includes(field)) {
            if (isNaN(value)) {
              message.error('숫자만 입력 가능합니다.');
              return false;
            }
            return true;
          }
          if (field === 'purchaseConfig.purchaseBundle') {
            const purchaseConfig = params.data?.purchaseConfig;
            if (
              value === PurchaseBundleType.MAX &&
              (!purchaseConfig?.purchaseMaxBundle || isNaN(purchaseConfig?.purchaseMaxBundle))
            ) {
              message.error('최대 묶음 값을 입력해주세요.');
              return false;
            }
          }
          return true;
        },
      }),

    onCellValueChanged: (params: NewValueParams<InventoryTableRowData>) =>
      onCellValueChangedForInventory(params, (id, value) =>
        params.colDef.field
          ? updateProductInventoryForField(id, params.colDef.field, value)
          : Promise.reject(new Error('필드 정보가 없습니다.'))
      ),
  },
};

/**
 * 재고 필드 유효성 검사
 */
export const cellValidatorForInventoryUnit = (params: any) => {
  const {
    data,
    colDef: { field },
  } = params;
  const targetCellData: number | undefined = get(data, `inventory.uom.${field}`);
  return targetCellData ? true : false;
};

/**
 * DailyOutboundTransactionTable의 셀 편집 관련 핸들러 모음
 * - valueSetter: 셀 값 변경 시 호출되는 함수
 * - onCellValueChanged: 셀 값 변경 후 호출되는 함수
 */
export const dailyOutboundTransactionTableHandlers = {
  cost: {
    valueSetter: (params: ValueSetterParams<ProcessedOutboundTransaction>) =>
      inventoryFieldAgValueSetter(params, {
        nullable: true,
        sanitizer: (value) => Number(value),
        validator: (value) => {
          const isValid = cellValidatorForInventoryUnit(params);
          if (!isValid) {
            message.error('재고 설정이 안된 단위 입니다.');
            return false;
          }
          if (isNaN(value)) {
            message.error('숫자만 입력 가능합니다.');
            return false;
          }
          return true;
        },
      }),
    onCellValueChanged: async (
      params: NewValueParams<ProcessedOutboundTransaction>,
      unit: ProductInventoryUnit,
      date: string
    ) => {
      const { newValue, oldValue } = params;
      if (newValue === oldValue) return;
      const { inventoryId } = params.data;
      if (!inventoryId) return;
      const updatedAt = formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss+0900");
      try {
        await setDailyInventoryChanges(date, {
          changes: {
            [inventoryId]: {
              [unit]: newValue
                ? {
                    cost: newValue,
                    updatedAt,
                  }
                : deleteField(),
            },
          },
        });
        message.success('일일 재고 매입가 변경 내역 편집 완료');
      } catch (error) {
        console.error(error);
        const description = errorObjectToString(error);
        notification.error({
          message: `일일 재고 매입가 변경 내역 변경에 실패했습니다.`,
          description,
        });
      }
    },
  },
};
